import { prettifyAndLocalizeObjectType } from '../helpers/object-types';

const { useEffect, useState } = wp.element;

/**
 * Hook for the Object Type Selector functionality, currently only used on search pages when using Qala Elastic Search to render the search page page.
 *
 * @param {Object}  args                        Args for the hook
 * @param {string}  args.template
 * @param {boolean} args.showObjectTypeSelector
 * @param {string}  args.objectType
 * @param {Array}   args.overrideTemplates
 * @param {Array}   args.usedObjectTypes
 * @return {void}
 */
export const useObjectTypeSelector = ( {
	showObjectTypeSelector,
	template,
	objectType,
	overrideTemplates,
	usedObjectTypes,
} ) => {
	const areOverrideTemplatesSet = overrideTemplates && Array.isArray( overrideTemplates ) && overrideTemplates.length > 0 && overrideTemplates[ 0 ]?.object_type;

	// Make sure the first object_type in overrideTemplates is the default one (if set). If not then the main object_type is the default one.
	const [ overrideObjectType, setOverrideObjectType ] = useState( areOverrideTemplatesSet ? overrideTemplates[ 0 ].object_type : objectType );
	const [ specificObjectTypeTemplate, setSpecificObjectTypeTemplate ] = useState( template );
	const [ allObjectTypes, setAllObjectTypes ] = useState( [] );
	const [ shouldReRunQuery, setShouldReRunQuery ] = useState( false );

	/**
	 * Click handler for object type selector buttons.
	 *
	 * @param {string} whichObjectType Object type to search for.
	 */
	const searchOnClick = whichObjectType => {
		maybeOverrideTemplate( whichObjectType );

		wp.hooks.doAction( 'qalaElasticCore.action.doQuery', { query: null, options: {} } );
	};

	/**
	 * Maybe override the template for a specific object type.
	 *
	 * @param {string} whichObjectType Object type we're searching for.
	 */
	const maybeOverrideTemplate = whichObjectType => {
		if ( whichObjectType !== overrideObjectType ) {
			setOverrideObjectType( whichObjectType );
		}

		const overrideTemplate = ( overrideTemplates || [] ).find( singleOverrideTemplate => singleOverrideTemplate.object_type === whichObjectType );

		if ( ( whichObjectType === 'product' || whichObjectType === 'product_variation' ) && ! overrideTemplate ) {
			// Implicitly override the template for products and product variations unless there's an override template.
			setSpecificObjectTypeTemplate( 'qec-modern-product' );
		} else if ( overrideTemplate ) {
			setSpecificObjectTypeTemplate( overrideTemplate.template );
		} else {
			setSpecificObjectTypeTemplate( template );
		}

		// Set the clicked object type as active.
		setAllObjectTypes(
			allObjectTypes.map( singleObjectType => {
				return {
					...singleObjectType,
					isActive: singleObjectType.name === whichObjectType,
				};
			} )
		);
	};

	/**
	 * Callback for the qalaElasticCore.action.onResult.facets hook.
	 *
	 * @param {Object} facets Facet objects.
	 * @return {Object} Mapped object types.
	 */
	const mapObjectTypes = facets => {
		if ( ! facets.object_type || ! facets.object_type[ 0 ] || ! facets.object_type[ 0 ].data ) {
			return [];
		}

		const relevantFacets = facets.object_type[ 0 ].data.filter( singleObjectType => usedObjectTypes.includes( singleObjectType.value ) );

		// Check if overrideObjectType has any results from looking at facets data. If not, we find another facet (with most results)
		// if it exists and run a query with that. If there are no other facets, it means it's a 404 so don't do anything.
		if ( relevantFacets.length > 0 && ! relevantFacets.find( singleObjectType => singleObjectType.value === overrideObjectType ) ) {
			// If this is the case, use the first facet as the overrideObjectType.
			setOverrideObjectType( facets.object_type[ 0 ].data[ 0 ].value );
			setShouldReRunQuery( true );
			return;
		}

		// Now let's make sure the ordering of object types is the same as the ordering of overrideTemplates (all undefined object types in overrideTemplates should be last).
		const orderedObjectTypes = [];
		if ( areOverrideTemplatesSet ) {
			overrideTemplates.forEach( singleOverrideTemplate => {
				const foundObjectType = relevantFacets.find( singleObjectType => singleObjectType.value === singleOverrideTemplate.object_type );
				if ( foundObjectType ) {
					orderedObjectTypes.push( foundObjectType );
				}
			} );
		}

		// All the object types that are not in overrideTemplates should be last.
		relevantFacets.forEach( singleObjectType => {
			if ( ! areOverrideTemplatesSet || ! overrideTemplates.find( singleOverrideTemplate => singleOverrideTemplate.object_type === singleObjectType.value ) ) {
				orderedObjectTypes.push( singleObjectType );
			}
		} );

		setAllObjectTypes(
			prettifyAndLocalizeObjectType( orderedObjectTypes.map( singleObjectType => {
				return {
					name: singleObjectType.value,
					count: singleObjectType.count,
					isActive: singleObjectType.value === overrideObjectType,
				};
			} ) )
		);
	};

	/**
	 * Callback for the qalaElasticCore.filter.query hook.
	 *
	 * @param {Object} query Elasticsearch query to filter
	 * @return {Object} Filtered query
	 */
	const overrideObjectTypeCallback = query => {
		// Find the object with key: object_type inside the query.options.filters.all array and replace it with overrideObjectType.
		const index = query.options.filters.all.findIndex( filter => Object.keys( filter )[ 0 ] === 'object_type' );
		if ( index !== -1 ) {
			query.options.filters.all[ index ].object_type = overrideObjectType;
		}

		return query;
	};

	useEffect( () => {
		if ( ! showObjectTypeSelector ) {
			return;
		}
		// filter query should be before doQuery doAction.
		wp.hooks.addFilter( 'qalaElasticCore.filter.query', 'QalaElasticCoreResultsBlock', function( query ) {
			// Make sure we have object_type under facets if showObjectTypeSelector is true.
			if ( ! query.options.facets ) {
				query.options.facets = {};
			}

			query.options.facets.object_type = { type: 'value', size: 100 };

			if ( ! query.options.disjunctiveFacets ) {
				query.options.disjunctiveFacets = [ 'object_type' ];
			} else {
				query.options.disjunctiveFacets.push( 'object_type' );
			}

			return query;
		}, 2 );
	}, [] );

	useEffect( () => {
		if ( ! showObjectTypeSelector ) {
			return;
		}

		// Make sure we override the template initially if needed
		maybeOverrideTemplate( overrideObjectType );
	}, [ showObjectTypeSelector, overrideObjectType ] );

	useEffect( () => {
		if ( specificObjectTypeTemplate === template ) {
			return;
		}

		setSpecificObjectTypeTemplate( template );
	}, [ template ] );

	useEffect( () => {
		if ( ! showObjectTypeSelector ) {
			return;
		}

		wp.hooks.removeFilter( 'qalaElasticCore.filter.query', 'QalaElasticCore_useObjectTypeSelector', overrideObjectTypeCallback, 2 );
		wp.hooks.addFilter( 'qalaElasticCore.filter.query', 'QalaElasticCore_useObjectTypeSelector', overrideObjectTypeCallback, 2 );

		wp.hooks.removeAction( 'qalaElasticCore.action.onResult.facets', 'qalaElasticCore_useObjectTypeSelector', mapObjectTypes, 20 );
		wp.hooks.addAction( 'qalaElasticCore.action.onResult.facets', 'qalaElasticCore_useObjectTypeSelector', mapObjectTypes, 20 );

		if ( shouldReRunQuery ) {
			wp.hooks.doAction( 'qalaElasticCore.action.doQuery', { query: null, options: {} } );
			setShouldReRunQuery( false );
		}
	}, [ overrideObjectType, shouldReRunQuery ] );

	return {
		allObjectTypes,
		specificObjectTypeTemplate,
		searchOnClick,
	};
};
